import styled from 'foundations-theming/utils';
import memoizeLast from 'transmute/memoizeLast';
import compose from 'transmute/compose';

// @ts-expect-error module not typed
import CalleeErrorBoundary from './callee-error/CalleeErrorBoundary';
import NoCalleesPrompt from './callee-numbers/components/NoCalleesPrompt';
import CallSettingsNumbersWorkspace from './callee-numbers/components/CallSettingsNumbersWorkspace';
// @ts-expect-error module not typed
import EnsureCallingAccess from './calling-appeals/components/EnsureCallingAccess';
import { getData, isSucceeded } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getCallableObjects, getCallablePhoneNumberProperties, getHasAssociatedObjects, getPhoneNumberProperties } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { getCallableObjectAndAssociations } from 'calling-lifecycle-internal/callee-omnibus/operators/calleeOmnibusResultGetters';
import { View } from 'foundations-components/transitional/components/View';
// @ts-expect-error module not typed
import withCalleeOmnibus from './callee-omnibus/hoc/withCalleeOmnibus';
import withCallingAccess from './calling-appeals/hoc/withCallingAccess';
import withWidgetState from './widget-state/hoc/withWidgetState';
// @ts-expect-error module not typed
import withAccessLevelPrefetch from './access-level/hoc/withAccessLevelPrefetch';
import FormattedMessage from 'I18n/components/FormattedMessage';
import CallingOnboardingWorkspace from './callee-numbers/components/CallingOnboardingWorkspace';
import { useHubSpotCallingUsage } from 'calling-lifecycle-internal/calls-v1-settings-usage/hooks/useHubSpotCallingUsage';
import { getHubspotProvidedNumbers, getPhoneNumbers, getHubspotSubAccountNumbers } from './callee-omnibus/operators/settingsOmnibusResultGetters';
import { hasCallingMinutesPerPortal } from 'calling-orchestration-shared-library/hub-info/utils/getCallingLimits';
import { hasPaidSeatAccess } from 'calling-orchestration-shared-library/hub-info/utils/hasCallingFeature';
import { getIsThirdPartyCallingPopoverInUse } from 'calling-internal-common/utils/isThirdPartyCallingPopoverInUse';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "CallWorkspacePopover__Wrapper",
  componentId: "sc-1mdn8ny-0"
})(["max-width:615px;"]);
const CallWorkspacePopover = props => {
  const hasCallingMinutes = hasCallingMinutesPerPortal();
  const userHasPaidSeat = hasPaidSeatAccess();
  const {
    minutesUsage
  } = useHubSpotCallingUsage({
    hasCallingMinutes,
    userHasPaidSeat
  });
  const {
    asyncSettingsOmnibus,
    appIdentifier,
    gates
  } = props;
  const data = getData(asyncSettingsOmnibus);
  const fromNumbers = getPhoneNumbers(data);
  const hubspotSubAccountNumbers = getHubspotSubAccountNumbers(fromNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(fromNumbers);
  const channelAssignedPhoneNumbers = hubspotProvidedNumbers.concat(hubspotSubAccountNumbers);
  const hasCallableRecords = memoizeLast(calleesData => {
    const hasAssociatedObjects = getHasAssociatedObjects(calleesData);
    if (hasAssociatedObjects) {
      return true;
    }
    const callableObjects = getCallableObjects(calleesData);
    return callableObjects && callableObjects.some(callableObject => {
      const phoneNumberProperties = getPhoneNumberProperties(callableObject);
      return getCallablePhoneNumberProperties(phoneNumberProperties).size > 0;
    });
  });
  const renderNoCalleesMessage = () => {
    const {
      objectTypeId,
      subjectId,
      localSelectedCallProvider
    } = props;
    const isThirdPartyCallingPopoverInUse = getIsThirdPartyCallingPopoverInUse(gates, localSelectedCallProvider);
    return /*#__PURE__*/_jsx(NoCalleesPrompt, {
      subjectId: Number(subjectId),
      objectTypeId: objectTypeId,
      shouldShowAddNumber: false,
      isThirdPartyCallingPopoverInUse: isThirdPartyCallingPopoverInUse
    });
  };
  const renderNoFromNumberAccess = () => {
    return /*#__PURE__*/_jsx(View, {
      padding: "250",
      maxInlineSize: "300px",
      children: /*#__PURE__*/_jsx("strong", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "callee-selection.noAccessToFromNumber"
        })
      })
    });
  };
  const renderCallees = () => {
    const {
      onCallButtonClick,
      asyncCalleeOmnibus,
      widgetState,
      localSelectedCallProvider,
      selectedCallMethod,
      scopes,
      selectedFromNumber,
      isKnownVisitor,
      visitorPhoneNumber,
      ticketId,
      threadId,
      isUngatedForRedesign
    } = props;
    const calleeOmnibusData = getData(asyncCalleeOmnibus);
    const calleesData = calleeOmnibusData && getCallableObjectAndAssociations(calleeOmnibusData);
    const hasRecords = hasCallableRecords(calleesData);
    return /*#__PURE__*/_jsx(_Fragment, {
      children: !isSucceeded(asyncCalleeOmnibus) || hasRecords || visitorPhoneNumber ? /*#__PURE__*/_jsx(View, {
        "data-selenium-test": "call-settings-numbers-options",
        children: /*#__PURE__*/_jsx(CallSettingsNumbersWorkspace, {
          appIdentifier: appIdentifier,
          asyncCalleeOmnibus: asyncCalleeOmnibus,
          asyncSettingsOmnibus: asyncSettingsOmnibus,
          minutesUsage: minutesUsage,
          onCallButtonClick: onCallButtonClick,
          scopes: scopes,
          selectedCallMethod: selectedCallMethod,
          selectedCallProvider: localSelectedCallProvider,
          defaultFromNumber: selectedFromNumber,
          visitorPhoneNumber: visitorPhoneNumber,
          isKnownVisitor: isKnownVisitor,
          widgetState: widgetState,
          channelAssignedPhoneNumbers: channelAssignedPhoneNumbers,
          gates: gates,
          ticketId: ticketId,
          threadId: threadId,
          isUngatedForRedesign: isUngatedForRedesign
        })
      }) : renderNoCalleesMessage()
    });
  };
  const {
    callingAccess,
    limits,
    scopes
  } = props;
  return /*#__PURE__*/_jsx(Wrapper, {
    children: /*#__PURE__*/_jsx(CalleeErrorBoundary, {
      appName: "CALLING",
      children: /*#__PURE__*/_jsx(EnsureCallingAccess, {
        appIdentifier: appIdentifier,
        useFullWidth: false,
        scopes: scopes,
        callingAccess: callingAccess,
        children: /*#__PURE__*/_jsx(CallingOnboardingWorkspace, {
          appIdentifier: appIdentifier,
          asyncSettingsOmnibus: asyncSettingsOmnibus,
          gates: gates,
          limits: limits,
          scopes: scopes,
          children: channelAssignedPhoneNumbers.size > 0 ? renderCallees() : renderNoFromNumberAccess()
        })
      })
    })
  });
};
export default compose(withCalleeOmnibus, withCallingAccess, withWidgetState, withAccessLevelPrefetch)(CallWorkspacePopover);